/**
 * Global "file picker / camera capture is open" marker.
 *
 * Why this exists:
 *  - On iOS Safari and the Despia native WebView, opening a system camera
 *    via <input type="file" capture="environment"> sends the page into the
 *    `hidden` visibility state.
 *  - When the user finishes (taps "Use Photo" or "Cancel"), the page is
 *    `visible` again. From the SplashGate's perspective this looks exactly
 *    like the app was sent to the background and resumed — and it then
 *    shows the full "Welcome back" splash on top of the scanner page.
 *  - Visually this looks like the app threw the user back to the menu /
 *    loading screen after taking a photo.
 *
 * Solution: any code that opens a file/camera picker calls
 * `markFileCaptureStart()` immediately before triggering the input. The
 * SplashGate's `visibilitychange` handler skips the resume splash while
 * this marker is "fresh" (within the camera-pick window).
 *
 * The marker is intentionally simple, time-based and cleared on the next
 * focus, so a stuck flag can't keep the splash suppressed forever.
 */

const WINDOW_MS = 60_000; // generous: camera + permission prompts can take a while

interface CaptureGateState {
  activeUntil: number;
}

function getState(): CaptureGateState {
  if (typeof window === "undefined") return { activeUntil: 0 };
  const w = window as unknown as { __ultureFileCapture?: CaptureGateState };
  if (!w.__ultureFileCapture) w.__ultureFileCapture = { activeUntil: 0 };
  return w.__ultureFileCapture;
}

export function markFileCaptureStart(): void {
  getState().activeUntil = Date.now() + WINDOW_MS;
}

export function clearFileCaptureMark(): void {
  getState().activeUntil = 0;
}

export function isFileCaptureActive(): boolean {
  return Date.now() < getState().activeUntil;
}
